import React, { useEffect, useState } from 'react';
import { C, ACCENT } from '../../theme';
import { digitalApi as api } from './api';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../components/Toast';
import { Upload } from 'lucide-react';

// Gemius/GDE ZE-code taxonomy, phase 1 of 3 (2026-10-05) — data model, import, mapping. No ID
// generation and no export yet (phase 2/3). Every sheet is parsed client-side via the real `xlsx`
// npm package, loaded with a dynamic `import('xlsx')` (fixed 2026-10-05, twice: first replacing a
// `import('https://cdn.sheetjs.com/...')` call — which doesn't work at all under CRA/webpack, since
// a non-literal specifier forces webpack's dynamic-require fallback, which can't resolve a URL —
// then switching from a static `import * as XLSX from 'xlsx'` to this dynamic one so the ~100KB+
// library isn't added to the app's main bundle for every user, only those who open this page. The
// same CDN pattern existed in several OTHER bulk-import features elsewhere in this app — fixed
// there too, see CLAUDE.md) — the backend only ever receives already-parsed JSON rows, never a raw
// file.

async function parseSheet(file, sheetName) {
  const XLSX = await import('xlsx');
  const buf = await file.arrayBuffer();
  const wb = XLSX.read(buf, { type: 'array' });
  const name = sheetName && wb.SheetNames.includes(sheetName) ? sheetName : wb.SheetNames[0];
  const sheet = wb.Sheets[name];
  const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '', raw: true });
  if (!rows.length) return { header: [], rows: [] };
  return { header: rows[0].map(h => String(h).trim()), rows: rows.slice(1).filter(r => r.some(c => c !== '')) };
}

// The Planned Data file is large (real file ~5.8MB, ~65K rows) — parsing it on the main thread
// would freeze the browser tab for the duration (confirmed ~14s for the real file in Node). Runs
// in a Web Worker instead (supported natively by react-scripts 5 / webpack 5, no eject needed) so
// the UI stays responsive; the worker itself also restricts parsing to just the two real sheets
// and uses SheetJS's faster "dense" array mode, since genuinely parsing only one column isn't
// possible for the xlsx format (row structure has to be parsed regardless).
function parsePlannedDataFile(file) {
  return new Promise(async (resolve, reject) => {
    const worker = new Worker(new URL('./plannedDataParser.worker.js', import.meta.url));
    worker.onmessage = (e) => {
      worker.terminate();
      if (e.data.ok) resolve(e.data.result);
      else reject(new Error(e.data.error || 'Failed to parse the Planned Data file'));
    };
    worker.onerror = (err) => { worker.terminate(); reject(new Error(err.message || 'Worker failed')); };
    const buffer = await file.arrayBuffer();
    worker.postMessage({ buffer, fileName: file.name }, [buffer]);
  });
}

function mapRows(header, rows, colMap) {
  // colMap: { destField: 'Sheet Header Name' }
  const idx = {};
  Object.entries(colMap).forEach(([dest, h]) => { idx[dest] = header.indexOf(h); });
  return rows.map(r => {
    const out = {};
    Object.entries(idx).forEach(([dest, i]) => { out[dest] = i >= 0 ? r[i] : null; });
    return out;
  });
}

function PreviewBox({ preview, onConfirm, onCancel, confirming }) {
  if (!preview) return null;
  return (
    <div style={{ marginTop: 14, padding: '14px 16px', background: '#f4f8fb', border: '1px solid #dce8f0', borderRadius: 8 }}>
      <div style={{ display: 'flex', gap: 20, fontSize: 13, marginBottom: preview.rejected.length ? 10 : 0 }}>
        <span><b style={{ color: '#2a8a5a' }}>{preview.new}</b> new</span>
        <span><b style={{ color: ACCENT }}>{preview.changed}</b> changed</span>
        <span><b style={{ color: '#8aa0b8' }}>{preview.unchanged}</b> unchanged</span>
        <span><b style={{ color: '#a02020' }}>{preview.rejected.length}</b> rejected</span>
      </div>
      {preview.rejected.length > 0 && (
        <div style={{ maxHeight: 140, overflowY: 'auto', fontSize: 11, color: '#a02020', marginBottom: 10 }}>
          {preview.rejected.map((r, i) => <div key={i}>Row {r.row}: {r.reason}</div>)}
        </div>
      )}
      {!preview.committed && (
        <div style={{ display: 'flex', gap: 10 }}>
          <button style={C.btnSecondary} onClick={onCancel} disabled={confirming}>Cancel</button>
          <button style={C.btnPrimary} onClick={onConfirm} disabled={confirming || (preview.new === 0 && preview.changed === 0)}>
            {confirming ? 'Importing…' : `Confirm import (${preview.new + preview.changed} row(s))`}
          </button>
        </div>
      )}
      {preview.committed && <div style={{ ...C.feedback('success'), fontSize: 12 }}>Imported.</div>}
    </div>
  );
}

function ImportCard({ title, description, accept, onParse, onDryRun, onCommit }) {
  const [parsedRows, setParsedRows] = useState(null);
  const [preview, setPreview] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState('');
  const { showToast } = useToast();

  async function handleFile(e) {
    const file = e.target.files[0];
    if (!file) return;
    setError(''); setPreview(null); setParsedRows(null);
    try {
      const rows = await onParse(file);
      setParsedRows(rows);
      const res = await onDryRun(rows);
      setPreview({ ...res, committed: false });
    } catch (err) {
      setError(err.message || 'Failed to parse/preview');
    }
    e.target.value = '';
  }

  async function handleConfirm() {
    setConfirming(true);
    try {
      const res = await onCommit(parsedRows);
      setPreview({ ...res, committed: true });
      showToast(`Imported ${res.new} new, ${res.changed} changed`, 'success');
    } catch (err) {
      setError(err.message || 'Import failed');
    } finally {
      setConfirming(false);
    }
  }

  return (
    <div style={{ padding: 18, background: 'rgba(255,255,255,0.75)', border: '1px solid #dce8f0', borderRadius: 10, marginBottom: 16 }}>
      <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 4, color: '#1a2a3a' }}>{title}</div>
      <div style={{ fontSize: 12, color: '#6a8aaa', marginBottom: 10 }}>{description}</div>
      <label style={{ ...C.btnSecondary, display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
        <Upload size={13} />Choose file
        <input type="file" accept={accept} style={{ display: 'none' }} onChange={handleFile} />
      </label>
      {error && <div style={{ ...C.feedback('danger'), marginTop: 10, fontSize: 12 }}>{error}</div>}
      <PreviewBox preview={preview} onConfirm={handleConfirm} onCancel={() => { setPreview(null); setParsedRows(null); }} confirming={confirming} />
    </div>
  );
}

function EntriesTab() {
  const [entries, setEntries] = useState([]);
  useEffect(() => { api.getTaxonomyEntries().then(setEntries).catch(() => {}); }, []);
  return (
    <div>
      <div style={{ fontSize: 12, color: '#8aa0b8', marginBottom: 10 }}>{entries.length} taxonomy entries</div>
      <table style={C.table}>
        <thead><tr>
          <th style={C.th}>Client</th><th style={C.th}>Brand</th><th style={C.th}>Sub-brand</th><th style={C.th}>Product</th><th style={C.th}>Category</th>
        </tr></thead>
        <tbody>
          {entries.map(e => (
            <tr key={e.id} style={C.tr}>
              <td style={C.td}>{e.client_name} <span style={C.mutedLabel()}>{e.client_code}</span></td>
              <td style={C.td}>{e.brand_name} <span style={C.mutedLabel()}>{e.brand_code}</span></td>
              <td style={C.td}>{e.sub_brand_name} <span style={C.mutedLabel()}>{e.sub_brand_code}</span></td>
              <td style={C.td}>{e.product_name} <span style={C.mutedLabel()}>{e.product_code}</span></td>
              <td style={C.td}>{e.category_name} <span style={C.mutedLabel()}>{e.category_code}</span></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PlatformFormatsTab() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.getTaxonomyPlatformFormats().then(setRows).catch(() => {}); }, []);
  return (
    <div>
      <div style={{ fontSize: 12, color: '#8aa0b8', marginBottom: 10 }}>{rows.length} taxonomy platform-formats</div>
      <table style={C.table}>
        <thead><tr>
          <th style={C.th}>Platform</th><th style={C.th}>Format</th><th style={C.th}>Code</th><th style={C.th}>Ad Type</th><th style={C.th}>Buying Type</th><th style={C.th}>GDE</th><th style={C.th}>PlanIQ link</th>
        </tr></thead>
        <tbody>
          {rows.map(r => (
            <tr key={r.id} style={C.tr}>
              <td style={C.td}>{r.platform_name}</td>
              <td style={C.td}>{r.format_name}</td>
              <td style={C.td}><span style={C.mutedLabel()}>{r.platform_format_code}</span></td>
              <td style={C.td}>{r.ad_type || '—'}</td>
              <td style={C.td}>{r.buying_type || '—'}</td>
              <td style={C.td}>{r.gde_flag ? 'Yes' : 'No'}</td>
              <td style={C.td}>{r.planiq_platform_id ? <span style={C.statusRow()}><span style={C.statusDot('success')} />linked</span> : <span style={{ color: '#8aa0b8' }}>unmapped</span>}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function MappingTab() {
  const [data, setData] = useState(null);
  const [search, setSearch] = useState('');
  const { showToast } = useToast();

  function load() {
    api.getTaxonomyPlatformMappingSuggestions().then(setData).catch(() => {});
  }
  useEffect(() => { load(); }, []);

  async function confirmSuggestion(s) {
    try {
      await api.setTaxonomyPlatformFormatLink(s.taxonomy_platform_format_id, s.platform_id);
      showToast('Mapping confirmed', 'success');
      load();
    } catch (e) { showToast('Failed to confirm mapping', 'danger'); }
  }

  async function assignManually(taxRowId, platformId) {
    try {
      await api.setTaxonomyPlatformFormatLink(taxRowId, platformId || null);
      showToast(platformId ? 'Mapping saved' : 'Mapping cleared', 'success');
      load();
    } catch (e) { showToast('Failed to save mapping', 'danger'); }
  }

  if (!data) return <div style={C.empty}>Loading…</div>;
  const filteredUnmatched = data.unmatched_platforms.filter(p => !search || p.platform_name.toLowerCase().includes(search.toLowerCase()));

  return (
    <div>
      <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 10, color: '#1a2a3a' }}>Suggested matches ({data.suggestions.length})</div>
      <table style={{ ...C.table, marginBottom: 24 }}>
        <thead><tr><th style={C.th}>PlanIQ Platform</th><th style={C.th}>Suggested Taxonomy Row</th><th style={C.th}>Code</th><th style={C.th} /></tr></thead>
        <tbody>
          {data.suggestions.map(s => (
            <tr key={s.taxonomy_platform_format_id} style={C.tr}>
              <td style={C.td}>{s.platform_name}</td>
              <td style={C.td}>{s.taxonomy_label}</td>
              <td style={C.td}><span style={C.mutedLabel()}>{s.platform_format_code}</span></td>
              <td style={C.td}><button style={C.btnPrimary} onClick={() => confirmSuggestion(s)}>Confirm</button></td>
            </tr>
          ))}
          {data.suggestions.length === 0 && <tr><td style={C.td} colSpan={4}>No suggested matches.</td></tr>}
        </tbody>
      </table>

      <div style={{ display: 'flex', gap: 24 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 10, color: '#1a2a3a' }}>Unmatched PlanIQ platforms ({data.unmatched_platforms.length})</div>
          <input placeholder="Search platforms…" value={search} onChange={e => setSearch(e.target.value)} style={{ padding: '6px 10px', border: '1px solid #c8d8e8', borderRadius: 6, fontSize: 12, marginBottom: 10, width: 220 }} />
          <table style={C.table}>
            <thead><tr><th style={C.th}>Platform</th><th style={C.th}>Assign to taxonomy row</th></tr></thead>
            <tbody>
              {filteredUnmatched.map(p => (
                <tr key={p.platform_id} style={C.tr}>
                  <td style={C.td}>{p.platform_name}</td>
                  <td style={C.td}>
                    <select defaultValue="" style={{ ...C.input, padding: '4px 8px', fontSize: 12 }} onChange={e => assignManually(e.target.value, p.platform_id)}>
                      <option value="" disabled>— choose —</option>
                      {data.unused_taxonomy_rows.map(t => <option key={t.id} value={t.id}>{t.label} ({t.platform_format_code})</option>)}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 10, color: '#1a2a3a' }}>Unused taxonomy rows ({data.unused_taxonomy_rows.length})</div>
          <table style={C.table}>
            <thead><tr><th style={C.th}>Taxonomy row</th><th style={C.th}>Code</th></tr></thead>
            <tbody>
              {data.unused_taxonomy_rows.map(t => (
                <tr key={t.id} style={C.tr}><td style={C.td}>{t.label}</td><td style={C.td}><span style={C.mutedLabel()}>{t.platform_format_code}</span></td></tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function ImportTab() {
  return (
    <div>
      <ImportCard
        title="Clients List (taxonomy entries)"
        description="Client, brand, sub-brand, product and category codes and names."
        accept=".xlsx,.xls"
        onParse={async file => {
          const { header, rows } = await parseSheet(file, 'Clients List');
          return mapRows(header, rows, {
            client_code: 'Client ID', client_name: 'Client Name',
            brand_code: 'Brand ID', brand_name: 'BrandName',
            sub_brand_code: 'SubBrand ID', sub_brand_name: 'SubBrand Name',
            product_code: 'Product ID', product_name: 'Product Name',
            category_code: 'Category ID', category_name: 'Category Name',
          });
        }}
        onDryRun={rows => api.importTaxonomyEntries(rows, true)}
        onCommit={rows => api.importTaxonomyEntries(rows, false)}
      />
      <ImportCard
        title="Platform List (taxonomy platform-formats)"
        description="Platform and format names, their 4-character code, ad type and buying type."
        accept=".xlsx,.xls"
        onParse={async file => {
          const { header, rows } = await parseSheet(file, 'Platform List');
          return mapRows(header, rows, {
            platform_code: 'Platform ID', platform_name: 'Platform',
            format_code: 'Format ID', format_name: 'Format',
            platform_format_code: 'Platform/Format ID',
            ad_type: 'Ad Type', buying_type: 'Buying type', gde_flag: 'GDE Monitoring',
          });
        }}
        onDryRun={rows => api.importTaxonomyPlatformFormats(rows, true)}
        onCommit={rows => api.importTaxonomyPlatformFormats(rows, false)}
      />
      <ImportCard
        title="Planned Data file (reserved campaign ids)"
        description="Upload your Planned Data file so PlanIQ knows which campaign numbers are already in use."
        accept=".xlsx,.xls"
        onParse={parsePlannedDataFile}
        onDryRun={rows => api.importTaxonomyReservedCampaignIds(rows, true)}
        onCommit={rows => api.importTaxonomyReservedCampaignIds(rows, false)}
      />
    </div>
  );
}

export default function TaxonomyPage() {
  const { canImportTaxonomy, taxonomyEnabled } = useAuth();
  const [tab, setTab] = useState('import');

  if (!taxonomyEnabled) return <div style={C.empty}>Taxonomy is not enabled for this organization.</div>;

  const tabs = [
    ...(canImportTaxonomy ? [['import', 'Import']] : []),
    ['entries', 'Entries'], ['platform-formats', 'Platform Formats'], ['mapping', 'Platform Mapping'],
  ];

  return (
    <div style={C.page}>
      <div style={{ marginBottom: 24 }}>
        <h1 style={C.title}>Taxonomy</h1>
        <div style={{ fontSize: 12, color: '#8aa0b8', marginTop: 4 }}>Gemius/GDE ZE-code reference data — phase 1: data model, import and mapping</div>
      </div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        {tabs.map(([key, label]) => (
          <button key={key} onClick={() => setTab(key)} style={{
            padding: '7px 16px', borderRadius: 20, border: `1px solid ${tab === key ? ACCENT : '#dce8f0'}`,
            background: tab === key ? `${ACCENT}15` : 'transparent', color: tab === key ? ACCENT : '#4a6a8a',
            fontSize: 12, fontWeight: 600, cursor: 'pointer',
          }}>{label}</button>
        ))}
      </div>
      {tab === 'import' && canImportTaxonomy && <ImportTab />}
      {tab === 'entries' && <EntriesTab />}
      {tab === 'platform-formats' && <PlatformFormatsTab />}
      {tab === 'mapping' && <MappingTab />}
    </div>
  );
}
